import React, { useState, useEffect } from 'react';
import './AdminComments.css';
import commentsService from '../../../services/comments.service';
import { useToast } from '../../../context/ToastContext';

const AdminComments = () => {
      const toast = useToast();

  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedComment, setSelectedComment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
    rejected: 0,
    avgRating: 0
  });

  useEffect(() => {
    fetchComments();
    fetchStats();
  }, []);

  const fetchComments = async () => {
    setLoading(true);
    try {
      const result = await commentsService.getAllComments();
      if (result.success) {
        setComments(result.comments);
      }
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const result = await commentsService.getCommentStats();
      if (result.success) {
        setStats(result.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleStatusChange = async (commentId, newStatus) => {
    try {
      const result = await commentsService.updateCommentStatus(commentId, newStatus);
      if (result.success) {
        setComments(prev => 
          prev.map(comment => 
            comment.id === commentId ? { ...comment, status: newStatus } : comment
          )
        );
        await fetchStats();
         toast.showInfo(result.message);
      }
    } catch (error) {
      console.error('Error updating comment status:', error);
      toast.showError('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (commentId) => {
    if (!deleteConfirm) {
      setDeleteConfirm(commentId);
      return;
    }

    try {
      const result = await commentsService.deleteComment(commentId);
      if (result.success) {
        setComments(prev => prev.filter(comment => comment.id !== commentId));
        await fetchStats();
        toast.showInfo(result.message);
      }
    } catch (error) {
      console.error('Error deleting comment:', error);
      toast.showError('Erreur lors de la suppression');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const viewCommentDetails = (comment) => {
    setSelectedComment(comment);
    setShowDetailModal(true);
  };

  const filteredComments = comments.filter(comment => {
    const matchesStatus = filterStatus === 'all' || comment.status === filterStatus;
    const matchesRating = filterRating === 'all' || comment.rating === parseInt(filterRating);
    const matchesSearch = 
      comment.author.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comment.comment.toLowerCase().includes(searchQuery.toLowerCase()) ||
      comment.productName?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesStatus && matchesRating && matchesSearch;
  });

  const getStatusColor = (status) => {
    switch(status) {
      case 'approved': return '#10b981';
      case 'pending': return '#f59e0b';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusLabel = (status) => {
    switch(status) {
      case 'approved': return 'Approuvé';
      case 'pending': return 'En attente';
      case 'rejected': return 'Rejeté';
      default: return status;
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date inconnue';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-DZ', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <div className="admin-comments-admin-comments">

      {/* Stats Cards */}
      <div className="admin-comments-stats-grid">
        <div className="admin-comments-stat-card admin-comments-stat-card--total">
          <div className="admin-comments-stat-value">{stats.total}</div>
          <div className="admin-comments-stat-label">Total Commentaires</div>
        </div>
        
        <div className="admin-comments-stat-card admin-comments-stat-card--approved">
          <div className="admin-comments-stat-value">{stats.approved}</div>
          <div className="admin-comments-stat-label">Approuvés</div>
        </div>
        
        <div className="admin-comments-stat-card admin-comments-stat-card--pending">
          <div className="admin-comments-stat-value">{stats.pending}</div>
          <div className="admin-comments-stat-label">En Attente</div>
        </div>
        
        <div className="admin-comments-stat-card admin-comments-stat-card--rating">
          <div className="admin-comments-stat-value">{stats.avgRating} ⭐</div>
          <div className="admin-comments-stat-label">Note Moyenne</div>
        </div>
      </div>

      {/* Filters */}
      <div className="admin-comments-comments-filters">
        <div className="admin-comments-filter-search">
          <svg className="admin-comments-search-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8" />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="text"
            placeholder="Rechercher par auteur, produit ou commentaire..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="admin-comments-search-input"
          />
        </div>

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="admin-comments-filter-select"
        >
          <option value="all">Tous les statuts</option>
          <option value="approved">Approuvés</option>
          <option value="pending">En attente</option>
          <option value="rejected">Rejetés</option>
        </select>

        <select
          value={filterRating}
          onChange={(e) => setFilterRating(e.target.value)}
          className="admin-comments-filter-select"
        >
          <option value="all">Toutes les notes</option>
          <option value="5">⭐⭐⭐⭐⭐</option>
          <option value="4">⭐⭐⭐⭐</option>
          <option value="3">⭐⭐⭐</option>
          <option value="2">⭐⭐</option>
          <option value="1">⭐</option>
        </select>
      </div>

      {/* Comments List */}
      {loading ? (
        <div className="admin-comments-loading-state">
          <div className="admin-comments-spinner"></div>
          <p>Chargement...</p>
        </div>
      ) : filteredComments.length === 0 ? (
        <div className="admin-comments-empty-state">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
          <h3>Aucun commentaire trouvé</h3>
          <p>Essayez de modifier vos filtres</p>
        </div>
      ) : (
        <div className="admin-comments-comments-list">
          {filteredComments.map(comment => (
            <div key={comment.id} className="admin-comments-comment-card">
              <div className="admin-comments-comment-header">
                <div className="admin-comments-comment-author-info">
                  <div className="admin-comments-author-avatar">
                    {comment.avatar || comment.author.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <div className="admin-comments-author-name">{comment.author}</div>
                    <div className="admin-comments-author-email">{comment.email}</div>
                  </div>
                </div>
                
                <span className={`admin-comments-status-badge admin-comments-status-badge--${comment.status}`}>
                  {getStatusLabel(comment.status)}
                </span>
              </div>

              <div className="admin-comments-comment-body">
                <div className="admin-comments-comment-rating">
                  {[...Array(5)].map((_, i) => (
                    <svg 
                      key={i} 
                      className={`admin-comments-star ${i < comment.rating ? 'admin-comments-star--filled' : ''}`}
                      viewBox="0 0 24 24" 
                      fill="currentColor"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                  ))}
                </div>
                
                <p className="admin-comments-comment-text">{comment.comment}</p>

                <div className="admin-comments-comment-meta">
                  <div className="admin-comments-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                    </svg>
                    {comment.productName || 'Produit inconnu'}
                  </div>
                  <div className="admin-comments-meta-item">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {formatDate(comment.createdAt)}
                  </div>
                </div>
              </div>

              <div className="admin-comments-comment-actions">
                {comment.status === 'pending' && (
                  <>
                    <button
                      onClick={() => handleStatusChange(comment.id, 'approved')}
                      className="admin-comments-btn-action admin-comments-btn-action--approve"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      Approuver
                    </button>
                    <button
                      onClick={() => handleStatusChange(comment.id, 'rejected')}
                      className="admin-comments-btn-action admin-comments-btn-action--reject"
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      Rejeter
                    </button>
                  </>
                )}
                
                {comment.status === 'approved' && (
                  <button
                    onClick={() => handleStatusChange(comment.id, 'pending')}
                    className="admin-comments-btn-action admin-comments-btn-action--secondary"
                  >
                    Mettre en attente
                  </button>
                )}
                
                {comment.status === 'rejected' && (
                  <button
                    onClick={() => handleStatusChange(comment.id, 'approved')}
                    className="admin-comments-btn-action admin-comments-btn-action--secondary"
                  >
                    Approuver
                  </button>
                )}

                <button
                  onClick={() => viewCommentDetails(comment)}
                  className="admin-comments-btn-action admin-comments-btn-action--secondary"
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                  Détails
                </button>

                <button
                  onClick={() => handleDelete(comment.id)}
                  className={`admin-comments-btn-action admin-comments-btn-action--delete ${deleteConfirm === comment.id ? 'admin-comments-btn-action--confirm' : ''}`}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                  {deleteConfirm === comment.id ? 'Confirmer ?' : 'Supprimer'}
                </button>
                
                {deleteConfirm === comment.id && (
                  <button
                    onClick={() => setDeleteConfirm(null)}
                    className="admin-comments-btn-action admin-comments-btn-action--secondary"
                  >
                    Annuler
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {showDetailModal && selectedComment && (
        <div className="admin-comments-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="admin-comments-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="admin-comments-modal-header">
              <h2>Détails du Commentaire</h2>
              <button className="admin-comments-modal-close" onClick={() => setShowDetailModal(false)}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="admin-comments-modal-body">
              <div className="admin-comments-detail-author">
                <div className="admin-comments-detail-avatar">
                  {selectedComment.avatar || selectedComment.author.substring(0, 2).toUpperCase()}
                </div>
                <div>
                  <div className="admin-comments-detail-name">{selectedComment.author}</div>
                  <div className="admin-comments-detail-email">{selectedComment.email}</div>
                  <div className="admin-comments-detail-rating">
                    {[...Array(5)].map((_, i) => (
                      <svg 
                        key={i} 
                        className={`admin-comments-star ${i < selectedComment.rating ? 'admin-comments-star--filled' : ''}`}
                        viewBox="0 0 24 24" 
                        fill="currentColor"
                      >
                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                      </svg>
                    ))}
                  </div>
                </div>
              </div>

              <div className="admin-comments-detail-section">
                <div className="admin-comments-detail-label">PRODUIT</div>
                <div className="admin-comments-detail-value admin-comments-detail-product">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                  </svg>
                  {selectedComment.productName || 'Produit inconnu'}
                </div>
              </div>

              <div className="admin-comments-detail-section">
                <div className="admin-comments-detail-label">COMMENTAIRE</div>
                <div className="admin-comments-detail-comment">{selectedComment.comment}</div>
              </div>

              <div className="admin-comments-detail-section">
                <div className="admin-comments-detail-label">DATE DE PUBLICATION</div>
                <div className="admin-comments-detail-value">
                  {formatDate(selectedComment.createdAt)}
                </div>
              </div>

              <div className="admin-comments-detail-section">
                <div className="admin-comments-detail-label">STATUT</div>
                <span className={`status-badge status-badge--${selectedComment.status}`}>
                  {getStatusLabel(selectedComment.status)}
                </span>
              </div>
            </div>

            <div className="admin-comments-modal-footer">
              <button
                onClick={() => {
                  handleStatusChange(selectedComment.id, 'approved');
                  setShowDetailModal(false);
                }}
                className="admin-comments-btn admin-comments-btn-approve"
              >
                Approuver
              </button>
              <button
                onClick={() => {
                  handleStatusChange(selectedComment.id, 'rejected');
                  setShowDetailModal(false);
                }}
                className="admin-comments-btn admin-comments-btn-reject"
              >
                Rejeter
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminComments;